import { StickableBootstrapTable } from 'components/Table/StickableBootstrapTable';
import { ColumnDefinition, getCustomRenderColumn, getImageColumn, getNameIdColumn, getStatusDesColumn, sortableColumn } from 'components/TableColumn/TableColumn';
import { useMemo } from 'react';
import i18n from 'i18n';
import { camelCase } from 'lodash';
import { ProductAvailability } from 'core/product/Product';
import styles from './creativeRelatedProductList.module.scss';
import { CreativeRelatedProductInfo, CreativeStatus } from 'core/creative/Creative';
import { approvalFormatter } from './CreativeListFormatters';

export enum CreativeRelatedProductListColumns {
  NAME_ID = 'id',
  PRODUCT_IMAGE = 'productImageUrl',
  PRODCUT_STATE = 'state',
  AVAILABILITY = 'availability',
  APPROVAL = 'approvalStatus',
  UTIME = 'updateTime'
}

type CreativeRelatedProductListType = {
  creativeRelatedProductInfo: CreativeRelatedProductInfo[];
  reviewCreatives: (adx: string, creativeIds: number[]) => void;
  ignoreColumns?: CreativeRelatedProductListColumns[];
};

export const CreativeRelatedProductList: React.FC<CreativeRelatedProductListType> = ({
  creativeRelatedProductInfo,
  reviewCreatives,
  ignoreColumns = []
}) => {

  const columns = useMemo(
    () => [
      getNameIdColumn(
        sortableColumn(
          CreativeRelatedProductListColumns.NAME_ID,
          i18n.t<string>('creativeRelatedProductList.headers.name'),
          true
        )
      ),
      getImageColumn(
        sortableColumn(
          CreativeRelatedProductListColumns.PRODUCT_IMAGE,
          i18n.t<string>('creativeRelatedProductList.headers.productImage'),
          false
        ),
        (record: CreativeRelatedProductInfo) => record.imgLink,
        {
          width: '75px'
        }
      ),
      getStatusDesColumn(
        sortableColumn(
          CreativeRelatedProductListColumns.PRODCUT_STATE,
          i18n.t<string>('creativeRelatedProductList.headers.creativeState'),
          false
        ),
        (record: CreativeRelatedProductInfo) => {
          let des = i18n.t<string>('product.status.active');
          let color = 'success';
          if (record.state === CreativeStatus.DEACTIVE) {
            des = i18n.t<string>('product.status.deactivated');
            color = 'black';
          }
          return {
            des,
            color
          };
        }
      ),
      getStatusDesColumn(
        sortableColumn(
          CreativeRelatedProductListColumns.AVAILABILITY,
          i18n.t<string>('creativeRelatedProductList.headers.availability'),
          false
        ),
        (record: CreativeRelatedProductInfo) => {
          let des = i18n.t<string>(
            `product.status.${camelCase(ProductAvailability.IN_STOCK)}`
          );
          let color = 'success';
          if (record.availability === ProductAvailability.OUT_OF_STOCK) {
            des = i18n.t<string>(
              `product.status.${camelCase(ProductAvailability.OUT_OF_STOCK)}`
            );
            color = 'black';
          }
          return {
            des,
            color
          };
        }
      ),
      getCustomRenderColumn(
        sortableColumn(
          CreativeRelatedProductListColumns.APPROVAL,
          i18n.t<string>('creativeRelatedProductList.headers.approval'),
          false
        ),
        (record: CreativeRelatedProductInfo) =>
          approvalFormatter(
            record,
            false,
            false,
            reviewCreatives
          )
      ),
      sortableColumn(
        CreativeRelatedProductListColumns.UTIME,
        i18n.t<string>('creativeRelatedProductList.headers.updateTime'),
        true
      )
    ].filter(
      (column: ColumnDefinition) => !ignoreColumns.includes(column.dataField)
    ),
    [ignoreColumns, reviewCreatives]
  );

  return (
    <div className={styles.creativeRelatedProductList}>
      <div className={styles.scrollContainer}>
        <StickableBootstrapTable<CreativeRelatedProductInfo>
          className={styles.table}
          keyField='id'
          data={creativeRelatedProductInfo}
          columns={columns}
          scrollByElementControl={false}
          hidePagination={true}
        />
      </div>
    </div>
  );
};
